import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { formatCurrency, getAlert, getData, getDataWithJoin } from "./SamuiUtils";
import { formatDateAndTime, formatThaiDateUi } from "./DateUtils";

const ExportInventories = async (data) => {
    if (!Array.isArray(data) || data.length === 0) {
        return getAlert("FAILED", "ไม่มีข้อมูลสินค้าคงเหลือ");
    }

    try {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet("สินค้าคงเหลือ");

        // เพิ่ม Header พร้อมกำหนดสไตล์
        worksheet.columns = [
            { header: "รหัสสินค้า", key: "Item_Code", width: 15 },
            { header: "ชื่อสินค้า", key: "Item_Name", width: 30 },
            { header: "คงเหลือ", key: "Item_Onhand", width: 15 },
            { header: "คลังสินค้า", key: "WH_Name", width: 20 },
            { header: "รายการล่าสุด", key: "Last_STC_Date", width: 25 },
        ];

        worksheet.getRow(1).eachCell((cell) => {
            cell.font = { bold: true, color: { argb: "FFFF0000" } }; // ตัวหนังสือสีแดง
            cell.fill = {
                type: "pattern",
                pattern: "solid",
                fgColor: { argb: "FFFFFF00" }, // สีเหลือง
            };
            cell.alignment = { vertical: "middle", horizontal: "center" }; // จัดตำแหน่งกลาง
        });

        // เพิ่มข้อมูล
        data.forEach((item) => {
            worksheet.addRow({
                Item_Code: item.Item_Code,
                Item_Name: item.Item_Name,
                Item_Onhand: formatCurrency(item.Item_Onhand),
                WH_Name: item.WH_Name,
                Last_STC_Date: formatDateAndTime(item.Last_STC_Date),
            });
        });

        // บันทึกไฟล์
        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buffer]), "สินค้าคงเหลือ.xlsx");
    } catch (error) {
        console.error("Error exporting inventories:", error);
        getAlert("FAILED", "ไม่สามารถ Export ข้อมูลได้");
    }
};

const ExportInventoriesWarehouse = async (data) => {
    if (!Array.isArray(data) || data.length === 0) {
        return getAlert("FAILED", "ไม่มีข้อมูลสินค้าคงเหลือแยกตามคลัง");
    }

    try {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet("สินค้าคงเหลือแยกตามคลัง");

        // เพิ่ม Header พร้อมกำหนดสไตล์
        worksheet.columns = [
            { header: "รหัสสินค้า", key: "Item_Code", width: 15 },
            { header: "ชื่อสินค้า", key: "Item_Name", width: 30 },
            { header: "คงเหลือ", key: "Item_Onhand", width: 15 },
            { header: "คลังสินค้า", key: "WH_Name", width: 20 },
            { header: "รายการล่าสุด", key: "Last_STC_Date", width: 25 },
        ];

        worksheet.getRow(1).eachCell((cell) => {
            cell.font = { bold: true, color: { argb: "FFFF0000" } }; // ตัวหนังสือสีแดง
            cell.fill = {
                type: "pattern",
                pattern: "solid",
                fgColor: { argb: "FFFFFF00" }, // สีเหลือง
            };
            cell.alignment = { vertical: "middle", horizontal: "center" }; // จัดตำแหน่งกลาง
        });

        // เพิ่มข้อมูล
        data.forEach((item) => {
            worksheet.addRow({
                Item_Code: item.Item_Code,
                Item_Name: item.Item_Name,
                Item_Onhand: formatCurrency(item.Item_Onhand),
                WH_Name: item.WH_Name,
                Last_STC_Date: formatDateAndTime(item.Last_STC_Date),
            });
        });

        // บันทึกไฟล์
        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buffer]), "สินค้าคงเหลือแยกตามคลัง.xlsx");
    } catch (error) {
        console.error("Error exporting inventories:", error);
        getAlert("FAILED", "ไม่สามารถ Export ข้อมูลได้");
    }
};

const ExportVansaleSalesReport = async (docId, docNo) => {
    if (!docId) {
        return getAlert("FAILED", "ไม่พบเลขเอกสารของ Picking List");
    }

    try {
        const fetchDataPkList = await getData(
            'API_0801_PKList_H',
            `Doc_Id = ${docId}`);
        const fetchDataWhDocH = await getDataWithJoin(
            ' WD_D.Item_Id, WD_D.Item_Name, SUM(WD_D.Item_Qty) AS Item_Qty ',
            ' API_1001_WHDOC_H WD_H ',
            ' LEFT JOIN API_1002_WHDOC_D WD_D ON (WD_H.WHDoc_Id = WD_D.WHDoc_Id) ',
            ` WHERE (WHDoc_Type = 3) AND (Picking_No = '${docNo}') GROUP BY WD_D.Item_Id,WD_D.Item_Name ORDER BY WD_D.Item_Id `
        );
        const fetchDataSoHForItem = await getDataWithJoin(
            ' SO_D.Item_Id, SO_D.Item_Name, SUM(SO_D.Item_Qty) AS Item_Qty ',
            ' API_0701_SO_H SO_H ',
            ' LEFT JOIN API_0702_SO_D SO_D ON (SO_H.Doc_ID = SO_D.Doc_ID) ',
            ` WHERE SO_H.Doc_Is_PickingList = ${docId} GROUP BY SO_D.Item_Id,SO_D.Item_Name ORDER BY SO_D.Item_Id `
        );
        const fetchDataSoHForPayTotal = await getDataWithJoin(
            ' SO_H.Doc_Status_Paid_Type, SUM(SO_H.Pay_Total) AS Pay_Total ',
            ' API_0701_SO_H SO_H ',
            ` WHERE SO_H.Doc_Is_PickingList = ${docId} `,
            ' GROUP BY SO_H.Doc_Status_Paid_Type '
        );
        const fetchDataPkListForPaymentType = await getDataWithJoin(
            `PK_H.Doc_Id, PK_H.Doc_No AS Pk_No, SO_H.Doc_No AS So_No, PK_D.AR_Id, PK_D.DL_Queue AS AR_Queue, PK_D.AR_Name, SO_H.Total_Before_Vat, SO_H.VAT, 
            SO_H.NetTotal, SO_H.Pay_Total, SO_H.Doc_Status_Paid_Type, 
            CASE
                WHEN SO_H.Doc_Status_Paid_Type = 1 THEN 'เงินสด'
                WHEN SO_H.Doc_Status_Paid_Type = 2 THEN 'โอน'
                WHEN SO_H.Doc_Status_Paid_Type = 3 THEN 'เช็ค'
                WHEN SO_H.Doc_Status_Paid_Type = 4 THEN 'เครดิต'
                WHEN SO_H.Doc_Status_Paid_Type = 5 THEN 'ค้างจ่าย'
                ELSE 'ไม่ระบุ'
            END AS PaymentType, 
            PK_H.Driver1, AR.Default_Price, AR.AR_SPrice_id, AR.AR_SPrice_use, PA_O.Item_Onhand`,
            `API_0801_PKList_H AS PK_H`,
            `LEFT JOIN API_0802_PKList_D AS PK_D ON (PK_H.Doc_Id = PK_D.Doc_Id)
            LEFT JOIN API_0701_SO_H AS SO_H ON (PK_D.SO_Id = SO_H.Doc_ID)
            LEFT JOIN Tb_Set_AR AS AR ON (AR.AR_Id = PK_D.AR_Id)
            LEFT JOIN WH_ARPAYMENT_Onhand AS PA_O ON (AR.AR_Id = PA_O.Item_Id)`,
            `WHERE PK_H.Doc_Id = ${docId}`
        );
        const fetchDataPkListDetailForWhDocH = await getDataWithJoin(
            `PK_D.AR_Id, WD_H.WHDoc_Id, WD_D.Item_Code`,
            `API_0802_PKList_D AS PK_D`,
            `LEFT JOIN API_1001_WHDOC_H AS WD_H ON (PK_D.AR_Id = WD_H.T_WH_Id)
            LEFT JOIN API_1002_WHDOC_D AS WD_D ON (WD_H.WHDoc_Id = WD_D.WHDoc_Id)`,
            `WHERE WD_H.WHDoc_Type = 5`
        );

        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet("รายงานขาย Vansale");

        worksheet.getCell("A1").value = "สายส่ง";
        worksheet.getCell("A1").font = { bold: true };
        worksheet.mergeCells("B1:C1");
        worksheet.getCell("B1").value = fetchDataPkList[0].DL_LineName;

        worksheet.getCell("B2").value = "น้ำแข็งเบิก";
        worksheet.getCell("B2").font = { bold: true };
        worksheet.getCell("C2").value = "น้ำแข็งขาย";
        worksheet.getCell("C2").font = { bold: true };

        worksheet.getCell("A3").value = "หลอดใหญ่";
        worksheet.getCell("A3").font = { bold: true };
        worksheet.getCell("A4").value = "หลอดเล็ก";
        worksheet.getCell("A4").font = { bold: true };
        worksheet.getCell("A5").value = "บดใหญ่";
        worksheet.getCell("A5").font = { bold: true };
        worksheet.getCell("A6").value = "บดเล็ก";
        worksheet.getCell("A6").font = { bold: true };

        worksheet.getCell("B3").value = formatCurrency(0);
        worksheet.getCell("B4").value = formatCurrency(0);
        worksheet.getCell("B5").value = formatCurrency(0);
        worksheet.getCell("B6").value = formatCurrency(0);
        for (const data of fetchDataWhDocH) {
            if (data.Item_Name === "หลอดใหญ่") {
                worksheet.getCell("B3").value = formatCurrency(data.Item_Qty);
            } else if (data.Item_Name === "หลอดเล็ก") {
                worksheet.getCell("B4").value = formatCurrency(data.Item_Qty);
            } else if (data.Item_Name === "บดใหญ่") {
                worksheet.getCell("B5").value = formatCurrency(data.Item_Qty);
            } else if (data.Item_Name === "บดเล็ก") {
                worksheet.getCell("B6").value = formatCurrency(data.Item_Qty);
            }
        }

        worksheet.getCell("C3").value = formatCurrency(0);
        worksheet.getCell("C4").value = formatCurrency(0);
        worksheet.getCell("C5").value = formatCurrency(0);
        worksheet.getCell("C6").value = formatCurrency(0);
        for (const data of fetchDataSoHForItem) {
            if (data.Item_Name === "หลอดใหญ่") {
                worksheet.getCell("C3").value = formatCurrency(data.Item_Qty);
            } else if (data.Item_Name === "หลอดเล็ก") {
                worksheet.getCell("C4").value = formatCurrency(data.Item_Qty);
            } else if (data.Item_Name === "บดใหญ่") {
                worksheet.getCell("C5").value = formatCurrency(data.Item_Qty);
            } else if (data.Item_Name === "บดเล็ก") {
                worksheet.getCell("C6").value = formatCurrency(data.Item_Qty);
            }
        }

        worksheet.getCell("A8").value = "หมายเหตุ";
        worksheet.getCell("A8").font = { bold: true };
        worksheet.mergeCells("B8:H8");

        worksheet.getCell("J1").value = "วันที่เอกสาร";
        worksheet.getCell("J1").font = { bold: true };
        worksheet.getCell("K1").value = formatThaiDateUi(fetchDataPkList[0].Doc_Date);

        worksheet.getCell("J2").value = "ช่องทางชำระเงิน";
        worksheet.getCell("J2").font = { bold: true };
        worksheet.getCell("K2").value = "ยอดเงิน";
        worksheet.getCell("K2").font = { bold: true };
        worksheet.getCell("J3").value = "เงินสด";
        worksheet.getCell("J3").font = { bold: true };
        worksheet.getCell("J4").value = "โอน";
        worksheet.getCell("J4").font = { bold: true };
        worksheet.getCell("J5").value = "เครดิต";
        worksheet.getCell("J5").font = { bold: true };
        worksheet.getCell("J6").value = "เช็ค";
        worksheet.getCell("J6").font = { bold: true };
        worksheet.getCell("J7").value = "ค้างจ่าย";
        worksheet.getCell("J7").font = { bold: true };
        worksheet.getCell("J8").value = "รวม";
        worksheet.getCell("J8").font = { bold: true };

        worksheet.getCell("K3").value = formatCurrency(0);
        worksheet.getCell("K4").value = formatCurrency(0);
        worksheet.getCell("K5").value = formatCurrency(0);
        worksheet.getCell("K6").value = formatCurrency(0);
        worksheet.getCell("K7").value = formatCurrency(0);

        let total = 0;

        for (const data of fetchDataSoHForPayTotal) {
            if (data.Doc_Status_Paid_Type === 1) {
                worksheet.getCell("K3").value = formatCurrency(data.Pay_Total);
            } else if (data.Doc_Status_Paid_Type === 2) {
                worksheet.getCell("K4").value = formatCurrency(data.Pay_Total);
            } else if (data.Doc_Status_Paid_Type === 3) {
                worksheet.getCell("K5").value = formatCurrency(data.Pay_Total);
            } else if (data.Doc_Status_Paid_Type === 4) {
                worksheet.getCell("K6").value = formatCurrency(data.Pay_Total);
            } else if (data.Doc_Status_Paid_Type === 5) {
                worksheet.getCell("K7").value = formatCurrency(data.Pay_Total);
            }

            total += data.Pay_Total;
        }

        worksheet.getCell("K8").value = formatCurrency(total);

        // ตั้งค่าหัวตารางหลัก
        const headers = [
            "หลอดใหญ่", "หลอดเล็ก", "บดใหญ่", "บดเล็ก", "คิว",
            "รายชื่อลูกค้า", "เลขลัง", "ราคา", "ยกมา", "จ่าย", "ค้างจ่าย"
        ];

        worksheet.addRow([]); // เพิ่มแถวเว้นว่าง
        const headerRow = worksheet.addRow(headers);

        // กำหนดสไตล์ให้กับ Header
        headerRow.eachCell((cell, colNumber) => {
            cell.font = { bold: true, color: { argb: "FF000000" } }; // สีดำ
            cell.fill = {
                type: "pattern",
                pattern: "solid",
                fgColor: { argb: "FFFFFFFF" } // พื้นหลังสีขาว
            };
            cell.alignment = { vertical: "middle", horizontal: "center" };
        });

        // วนลูปดึงข้อมูลการขายจาก `fetchDataPkListForPaymentType`
        for (const data of fetchDataPkListForPaymentType) {
            // ดึงข้อมูลขายของลูกค้าคนนี้
            const fetchDataPkListDetailForSell = await getDataWithJoin(
                ` SO_D.Item_Id, SO_D.Item_Name, SUM(SO_D.Item_Qty) AS Item_Qty `,
                ` API_0802_PKList_D AS PK_D `,
                ` LEFT JOIN API_0701_SO_H AS SO_H ON (PK_D.SO_Id = SO_H.Doc_ID) 
            LEFT JOIN API_0702_SO_D AS SO_D ON (SO_H.Doc_ID = SO_D.Doc_ID) `,
                ` WHERE SO_H.Doc_No = '${data.So_No}' 
            GROUP BY SO_D.Item_Id, SO_D.Item_Name 
            ORDER BY SO_D.Item_Id`
            );

            // กำหนดค่าเริ่มต้นเป็น 0
            let หลอดใหญ่ = 0, หลอดเล็ก = 0, บดใหญ่ = 0, บดเล็ก = 0;

            // วนลูปดึงค่าตามประเภทน้ำแข็ง
            for (const item of fetchDataPkListDetailForSell) {
                if (item.Item_Name === "หลอดใหญ่") {
                    หลอดใหญ่ = formatCurrency(item.Item_Qty);
                } else if (item.Item_Name === "หลอดเล็ก") {
                    หลอดเล็ก = formatCurrency(item.Item_Qty);
                } else if (item.Item_Name === "บดใหญ่") {
                    บดใหญ่ = formatCurrency(item.Item_Qty);
                } else if (item.Item_Name === "บดเล็ก") {
                    บดเล็ก = formatCurrency(item.Item_Qty);
                }
            }

            // กำหนดค่าอื่นๆ
            let คิว = data.AR_Queue;
            let รายชื่อลูกค้า = data.AR_Name;
            let เลขลัง = (data.AR_Id === fetchDataPkListDetailForWhDocH.AR_Id) ? fetchDataPkListDetailForWhDocH.Item_Code : '';
            let ราคา = formatCurrency(data.NetTotal);
            let ยกมา = '';
            let จ่าย = formatCurrency(data.Pay_Total);
            let ค้างจ่าย = formatCurrency(data.Item_Onhand);

            // เพิ่มแถวข้อมูลเข้า Excel
            worksheet.addRow([
                หลอดใหญ่, หลอดเล็ก, บดใหญ่, บดเล็ก, คิว,
                รายชื่อลูกค้า, เลขลัง, ราคา, ยกมา, จ่าย, ค้างจ่าย
            ]);
        }

        // เพิ่มเส้นขอบให้ทุกเซลล์ในทุกแถว (ทั้ง Header และข้อมูล)
        worksheet.eachRow((row) => {
            row.eachCell((cell) => {
                cell.border = {
                    top: { style: "thin", color: { argb: "FF000000" } },
                    left: { style: "thin", color: { argb: "FF000000" } },
                    bottom: { style: "thin", color: { argb: "FF000000" } },
                    right: { style: "thin", color: { argb: "FF000000" } }
                };
            });
        });

        // บันทึกไฟล์
        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buffer]), "รายงานขาย_Vansale.xlsx");
    } catch (error) {
        getAlert("FAILED", "ไม่สามารถ Export ข้อมูลได้");
    }
};

export { ExportInventories, ExportInventoriesWarehouse, ExportVansaleSalesReport };